/**
 * RoomCalendar — the unit-detail Belegungskalender.
 *
 * S4.1 MOVED THE MEANING OUT OF THIS FILE. The tenancy selection, the
 * per-month truth and the counting rules used to live here as a private copy
 * of the logic the Co-Living matrix carried separately. Both copies shared one
 * defect: a CANCELLED tenancy stayed eligible to supply a room's calendar, and
 * because a cancellation is only permitted at or just before move-in, such a
 * row carries a recent or future move-in date — exactly what wins the
 * newest-move-in tiebreak. It could therefore out-rank the live tenancy and
 * paint an occupied room free for every month. The logic now lives in
 * `utils/lifecycleCalendarTruth`, which both calendars import; that is the only
 * arrangement in which they cannot drift apart again.
 *
 * WHAT THIS FILE STILL OWNS is its own presentation — its month strip, its
 * badges, its row layout. Only the meaning moved.
 *
 * ITS COLOURS ARE NO LONGER ITS OWN (ROOM-CALENDAR-THEME-1). Until then every
 * surface and every text here was a dark literal, which is not a palette but
 * the ABSENCE of one: the card stayed `#13161f` in both themes while
 * `index.css`'s light-mode compatibility rules forced the text classes to
 * near-black (`html[data-theme="light"] .text-[#edf0f7] { color: #1a1a18 }`
 * and its two siblings). Dark text on a dark card measured 1.04:1 — the room
 * name was effectively invisible in Light Mode. Every colour now comes from a
 * theme token or from a palette step that carries its own `dark:` twin, the
 * same arrangement `components/RoomMap.js` adopted, so the global
 * compatibility rules no longer match this file and no longer need to.
 */
import React from "react";
import {
  getRoomOccupancyStatus,
  formatOccupancyStatusDe,
  isActiveRoom,
} from "../utils/unitOccupancyStatus";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../utils/unitDisplayId";
import { formatDateOnly } from "../utils/datetime";
import {
  PARTIAL_LABEL,
  TRANSITION_MARKER,
  UNKNOWN_STATE,
  buildCalendarAggregates,
  buildRoomCalendarCells,
  describeTransitions,
  potentialLossDisplay,
  resolveMonthAxis,
  sumCalendarAggregates,
} from "../utils/lifecycleCalendarTruth";

/**
 * The potential-loss badge, for a room row or for the unit header (R9 / P1-E).
 *
 * ONE COMPONENT FOR BOTH so the two cannot describe the same aggregate
 * differently — the unit figure is a roll-up of the room ones and a reader
 * comparing them must not meet two vocabularies.
 *
 * A complete figure renders exactly what it rendered before R9: the amount and
 * nothing else. Only an incomplete one gains anything, and what it gains is
 * never just a colour — the word `partiell` is in the visible text, and the
 * size of the gap is in the accessible name.
 */
function PotentialLossBadge({ aggregates, className }) {
  const loss = potentialLossDisplay(aggregates);
  return (
    <>
      <span
        data-loss-kind={loss.kind}
        title={loss.accessibleText || undefined}
        className={className}
      >
        {loss.amountLabel}
        {loss.partial ? (
          <span className="ml-[3px] font-normal opacity-80">· {PARTIAL_LABEL}</span>
        ) : null}
        {" pot. Verlust"}
        {loss.srText ? <span className="sr-only"> {loss.srText}</span> : null}
      </span>
      {loss.partial ? (
        // HOW BIG THE GAP IS, ON SCREEN. "CHF 9'000 · partiell" says something
        // is missing but not how much, which is not enough to act on. The
        // wording is the one from `potentialLossDisplay` — this only places it.
        //
        // `basis-full` takes its own line inside the existing flex-wrap row, so
        // no container and no layout had to change for it.
        <span
          data-loss-gap
          className="basis-full text-right text-[9px] font-normal text-muted-foreground"
        >
          {loss.gapText}
        </span>
      ) : null}
    </>
  );
}

const MONTH_PREVIEW_COUNT = 9;

/**
 * One variant per lifecycle state. What used to be the unnamed `free` is now
 * `free_unknown`, the contract's name for "free, and nothing explains why";
 * `free_planned` keeps its own slate.
 *
 * THE HUES ARE THE ONES THIS CALENDAR ALREADY HAD. ROOM-CALENDAR-THEME-1
 * changed how each colour is expressed, never which state wears it: green
 * stayed green, the notice red stayed red, reserved stayed violet. Re-assigning
 * a hue would be a product decision — a manager who reads "red = cancelled"
 * must not meet green after a theme pass — and the Co-Living matrix's
 * `STATE_CLASSES` groups them differently on purpose, so the two are
 * deliberately NOT unified here.
 *
 * Each is a palette step with its own `dark:` twin rather than a token,
 * because these carry business meaning the five semantic tokens cannot
 * express. `unknown` is the exception and borrows the neutral tokens: it must
 * never read as a vacancy — it says the month could not be read at all — and a
 * muted cell is the one thing nobody mistakes for an opportunity.
 */
const STATE_STYLES = {
  secure:
    "bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20",
  cancellable:
    "bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20",
  notice_given:
    "bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20",
  always_cancellable:
    "bg-sky-50 text-sky-800 border border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20",
  reserved:
    "bg-violet-50 text-violet-800 border border-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20",
  free_planned:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/25",
  free_unknown:
    "bg-rose-50/60 text-rose-800 border border-rose-200/70 dark:bg-rose-500/[0.07] dark:text-rose-300 dark:border-rose-500/15",
  [UNKNOWN_STATE]: "bg-muted text-muted-foreground border border-border",
};

/** Fails closed: an unrecognised state borrows the neutral variant, never a free one. */
function stateStyle(state) {
  return STATE_STYLES[state] || STATE_STYLES[UNKNOWN_STATE];
}

function roomDefaultMinStayMonths(room) {
  const v = room.default_minimum_stay_months ?? room.defaultMinimumStayMonths;
  const n = Number(v);
  return Number.isFinite(n) ? n : 3;
}

function roomDefaultNoticeMonths(room) {
  const v = room.default_notice_period_months ?? room.defaultNoticePeriodMonths;
  const n = Number(v);
  return Number.isFinite(n) ? n : 1;
}

function RoomCalendar({
  unit,
  rooms: allRooms = [],
  tenancies = null,
  isCollapsed = false,
  onToggleHeader = null,
  headerTrailingAction = null,
}) {
  const unitRooms = allRooms.filter(
    (room) => (room.unitId || room.unit_id) === (unit.unitId || unit.id)
  );

  const headerInteractive = typeof onToggleHeader === "function";
  const headerClass = `flex flex-wrap items-center gap-[10px] border-b border-border px-[14px] py-[11px] ${
    headerInteractive ? "cursor-pointer select-none hover:bg-muted" : ""
  }`;

  if (unitRooms.length === 0) {
    return (
      <div className="overflow-hidden rounded-[10px] border border-border bg-card">
        <div
          className={headerClass}
          onClick={headerInteractive ? onToggleHeader : undefined}
          onKeyDown={
            headerInteractive
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleHeader();
                  }
                }
              : undefined
          }
          role={headerInteractive ? "button" : undefined}
          tabIndex={headerInteractive ? 0 : undefined}
        >
          {headerInteractive ? (
            <span className="shrink-0 text-[10px] text-muted-foreground" aria-hidden>
              {isCollapsed ? "▼" : "▲"}
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] font-medium text-primary">{getUnitHeadingLabel(unit)}</p>
            <p className="mt-[2px] text-[11px] text-muted-foreground">{getUnitAddressSubtitle(unit)}</p>
          </div>
          {headerTrailingAction ? (
            <div className="ml-auto shrink-0">{headerTrailingAction}</div>
          ) : null}
        </div>
        {!isCollapsed ? (
          <p className="px-[14px] py-[12px] text-[11px] italic text-muted-foreground">Keine Rooms für diese Unit erfasst.</p>
        ) : null}
      </div>
    );
  }
  // ONE axis for the whole unit, anchored on the payload's own month keys (or
  // the Swiss business month when the payload has none) — never on the device's
  // calendar month, which is a month ahead of Zurich for part of every night.
  const monthAxis = resolveMonthAxis({
    tenancies,
    monthCount: MONTH_PREVIEW_COUNT,
  }).months;

  // Each room resolved ONCE. The old code rebuilt every room's timeline four
  // times over just to print four badges.
  const roomRows = unitRooms.map((room) => {
    const cells = buildRoomCalendarCells({ room, tenancies, months: monthAxis });
    return {
      room,
      cells,
      /**
       * CURRENT INVENTORY STATE — NOT A MONTH STATE (ROOM-ACT-4b).
       *
       * `is_active` is a bare boolean on `Room` with no companion timestamp
       * anywhere in the schema, so it says whether the room is in service NOW
       * and nothing about any particular month. It therefore reaches the ROW
       * and must never reach a cell: `cells` above and `aggregates` below are
       * built without it, exactly as before.
       *
       * Derived HERE, in the projection this block already builds per room,
       * so the two render sites below cannot answer the question differently.
       */
      isActive: isActiveRoom(room),
      aggregates: buildCalendarAggregates(cells, room.priceMonthly),
    };
  });

  const unitAggregates = sumCalendarAggregates(roomRows.map((r) => r.aggregates));

  const monthColTemplate = `repeat(${MONTH_PREVIEW_COUNT}, minmax(4.75rem, 4.75rem))`;

  return (
    <div className="overflow-hidden rounded-[10px] border border-border bg-card">
      <div
        className={headerClass}
        onClick={headerInteractive ? onToggleHeader : undefined}
        onKeyDown={
          headerInteractive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleHeader();
                }
              }
            : undefined
        }
        role={headerInteractive ? "button" : undefined}
        tabIndex={headerInteractive ? 0 : undefined}
      >
        {headerInteractive ? (
          <span className="shrink-0 text-[10px] text-muted-foreground" aria-hidden>
            {isCollapsed ? "▼" : "▲"}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] font-medium text-primary">{getUnitHeadingLabel(unit)}</p>
          <p className="mt-[2px] text-[11px] text-muted-foreground">{getUnitAddressSubtitle(unit)}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-[6px]">
          <span className="rounded-full border border-border bg-muted px-2 py-[2px] text-[9px] font-semibold text-muted-foreground">
            {unitRooms.length} Rooms
          </span>
          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-[2px] text-[9px] font-semibold text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-300">
            {unitAggregates.secure} sichere Monate
          </span>
          <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-[2px] text-[9px] font-semibold text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/15 dark:text-amber-300">
            {unitAggregates.cancellable} kündbare Monate
          </span>
          <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-[2px] text-[9px] font-semibold text-slate-700 dark:border-slate-500/25 dark:bg-slate-500/15 dark:text-slate-300">
            {unitAggregates.free} freie Monate
          </span>
          <PotentialLossBadge
            aggregates={unitAggregates}
            className="rounded-full border border-rose-200 bg-rose-100 px-2 py-[2px] font-mono text-[9px] font-semibold text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/15 dark:text-rose-300"
          />
          {headerTrailingAction}
        </div>
      </div>

      {!isCollapsed ? (
        <div className="px-0 pb-0 pt-0">
          {roomRows.map(({ room, cells, aggregates, isActive }, index) => {
            const occ = tenancies != null ? getRoomOccupancyStatus(room, tenancies) : null;

            // ACTIVITY COMES FIRST IN THE LEADING SEGMENT, the same order the
            // Co-Living matrix's `buildRoomMeta` uses. For a stood-down room
            // the occupancy label is a lie by construction: a retired room has
            // no tenancy, so it classifies as "frei" and this line announced it
            // as available capacity. Rent, move-in, minimum stay and notice
            // period are facts about the room and stay exactly as they were.
            const activityLabel = isActive
              ? occ != null
                ? formatOccupancyStatusDe(occ)
                : "—"
              : "Inaktiv";

            return (
              <div
                key={room.roomId || index}
                className="border-b border-border px-[14px] py-[12px] last:border-b-0"
              >
                <div className="mb-[8px] flex flex-col gap-[8px] sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    {/* ROOM-ACT-4b. The marker sits beside the NAME, not in
                        a tooltip and not in a colour. The tokens are the ones
                        this file already uses for its neutral `unknown` cell
                        (`STATE_STYLES` above) — the one place it deliberately
                        takes the theme's muted trio instead of a dark literal —
                        so the badge is theme-safe without adding a palette.
                        Only the padding is tightened, because this one sits
                        inline beside 12px text rather than in the pill row. */}
                    <p className="flex flex-wrap items-center gap-[6px] text-[12px] font-medium text-foreground">
                      <span className="min-w-0 break-words">
                        {room.roomName || room.name || `Zimmer ${index + 1}`}
                      </span>
                      {isActive ? null : (
                        <span className="shrink-0 rounded-full border border-border bg-muted px-[6px] py-px text-[9px] font-semibold text-muted-foreground">
                          Inaktiv
                        </span>
                      )}
                    </p>

                    <p className="mt-[1px] text-[10px] text-muted-foreground">
                      {activityLabel}
                      {room.priceMonthly
                        ? ` · CHF ${Number(room.priceMonthly).toLocaleString("de-CH")}`
                        : ""}
                      {room.moveInDate ? ` · Einzug ${room.moveInDate}` : ""}
                      {` · Mindestdauer ${roomDefaultMinStayMonths(room)}M`}
                      {` · Kündigung ${roomDefaultNoticeMonths(room)}M`}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-[4px] sm:justify-end">
                    <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-[2px] text-[9px] font-semibold text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-300">
                      {aggregates.secure} sichere Monate
                    </span>
                    <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-[2px] text-[9px] font-semibold text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/15 dark:text-amber-300">
                      {aggregates.cancellable} kündbare Monate
                    </span>
                    <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-[2px] text-[9px] font-semibold text-slate-700 dark:border-slate-500/25 dark:bg-slate-500/15 dark:text-slate-300">
                      {aggregates.free} freie Monate
                    </span>
                    <PotentialLossBadge
                      aggregates={aggregates}
                      className="rounded-full border border-rose-200 bg-rose-100 px-2 py-[2px] font-mono text-[9px] font-semibold text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/15 dark:text-rose-300"
                    />
                  </div>
                </div>

                <div className="-mx-[14px] overflow-x-auto px-[14px] pb-1 [scrollbar-width:thin]">
                  <div className="w-max min-w-full">
                    <div
                      className="mb-[4px] grid gap-[3px]"
                      style={{ gridTemplateColumns: monthColTemplate }}
                    >
                      {cells.map((cell, mi) => (
                        <div
                          key={`${room.roomId || index}-h-${mi}-${cell.monthKey}`}
                          className="text-center font-mono text-[8px] text-muted-foreground"
                        >
                          {cell.monthLabel}
                        </div>
                      ))}
                    </div>
                    <div className="grid gap-[3px]" style={{ gridTemplateColumns: monthColTemplate }}>
                      {cells.map((cell, mi) => {
                        // R8: the changes INSIDE this month. The cell keeps its
                        // own state, colour and word — this sits beside them.
                        const transitions = cell.transitions || [];
                        const transitionText = describeTransitions(transitions);
                        const cellBase = `${room.roomName || room.name || `Zimmer ${index + 1}`}, ${cell.monthLabel}: ${cell.label}`;
                        return (
                          <div
                            key={`${room.roomId || index}-c-${mi}-${cell.monthKey}`}
                            data-lifecycle-state={cell.state}
                            data-lifecycle-family={cell.family}
                            data-lifecycle-transitions={transitions.length || undefined}
                            aria-label={
                              transitionText
                                ? `${cellBase}. ${transitionText}`
                                : cellBase
                            }
                            title={
                              transitionText
                                ? `${cellBase} · ${transitionText}`
                                : undefined
                            }
                            className={`rounded-[5px] px-[2px] py-[5px] text-center font-mono text-[9px] font-semibold tracking-[0.2px] ${stateStyle(
                              cell.state
                            )}`}
                          >
                            {cell.label}
                            {transitions.length > 0 ? (
                              // A glyph, not a colour, and the wording travels
                              // in `aria-label` — so the marker survives both a
                              // colour-blind reader and one with no pointer.
                              <span className="ml-[2px] font-normal opacity-70" aria-hidden="true">
                                {TRANSITION_MARKER}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default RoomCalendar;
